import type { Etiqueta, MensajeProveedor, MensajesProveedores } from "@/lib/api";
import { sinTildes } from "@/lib/buscar";

/** Mensajes a proveedores (Mati, 2026-10-20): las variables van en el texto entre llaves, `{Nombre}`, sin distinguir
 * mayúsculas ni tildes. Mismos topes que `app/mensajes/reglas.py`. */
export const MAX_NOMBRE_VARIABLE = 40;
export const MAX_VALOR_VARIABLE = 300;
export const MAX_TEXTO_MENSAJE = 2000;
const PROVEEDOR = "proveedor";

const clave = (nombre: string) => sinTildes(nombre.trim());

/** clave → { nombre como se escribió, valor }. `proveedor` null = vista previa sin destinatario: queda la marca. */
export type ValoresMensaje = Map<string, { nombre: string; valor: string | null }>;

export function valoresMensaje(estado: MensajesProveedores, proveedor: string | null): ValoresMensaje {
  const m: ValoresMensaje = new Map();
  for (const v of [...estado.automaticas, ...estado.propias]) m.set(clave(v.nombre), { nombre: v.nombre, valor: v.valor });
  m.set(PROVEEDOR, { nombre: "Proveedor", valor: proveedor });
  return m;
}

export interface MensajeArmado {
  texto: string;
  vacias: string[]; // variables sin valor: quedan como {Nombre} en el texto
  desconocidas: string[]; // {Algo} que no es ninguna variable
}

export function armarMensaje(texto: string, valores: ValoresMensaje): MensajeArmado {
  const vacias = new Set<string>();
  const desconocidas = new Set<string>();
  const armado = texto.replace(/\{([^{}]+)\}/g, (marca, nombre: string) => {
    const k = clave(nombre);
    const v = valores.get(k);
    if (!v) {
      desconocidas.add(nombre.trim());
      return marca;
    }
    if (v.valor === null) {
      if (k !== PROVEEDOR) vacias.add(v.nombre);
      return marca;
    }
    return v.valor;
  });
  return { texto: armado, vacias: [...vacias], desconocidas: [...desconocidas] };
}

/** Los mensajes para un proveedor: el de cada etiqueta suya que tenga uno; si ninguna tiene, el general (si hay). */
export function mensajesDelProveedor(estado: MensajesProveedores | null, etiquetas: string[]): MensajeProveedor[] {
  if (!estado) return [];
  const suyas = new Set(etiquetas.map(clave));
  const propios = estado.mensajes.filter((m) => m.etiqueta !== null && suyas.has(clave(m.etiqueta)));
  if (propios.length > 0) return propios;
  return estado.mensajes.filter((m) => m.etiqueta_id === null);
}

export const tituloMensaje = (m: MensajeProveedor) => m.etiqueta ?? "General";

export const etiquetasSinMensaje = (estado: MensajesProveedores): Etiqueta[] =>
  estado.etiquetas.filter((e) => !estado.mensajes.some((m) => m.etiqueta_id === e.id));

export const nombresVariables = (estado: MensajesProveedores): string[] => [...estado.automaticas, ...estado.propias].map((v) => v.nombre);

export function insertarVariable(texto: string, nombre: string, inicio: number, fin: number): { texto: string; cursor: number } {
  const marca = `{${nombre}}`;
  return { texto: texto.slice(0, inicio) + marca + texto.slice(fin), cursor: inicio + marca.length };
}

export const conTexto = (link: string, texto: string) => `${link}?text=${encodeURIComponent(texto)}`;

/** `editando`: el id de la variable que se renombra (no choca consigo misma). */
export function validarNombreVariable(nombre: string, estado: MensajesProveedores, editando: number | null = null): string | null {
  const limpio = nombre.trim().replace(/\s+/g, " ");
  if (!limpio) return "Poné un nombre";
  if (limpio.length > MAX_NOMBRE_VARIABLE) return `Hasta ${MAX_NOMBRE_VARIABLE} caracteres`;
  if (/[{}]/.test(limpio)) return "Sin llaves";
  const auto = estado.automaticas.find((v) => clave(v.nombre) === clave(limpio));
  if (auto) return `«${auto.nombre}» ya es una variable del proyecto`;
  const otra = estado.propias.find((v) => v.id !== editando && clave(v.nombre) === clave(limpio));
  return otra ? `Ya hay una variable «${otra.nombre}»` : null;
}

export function validarTextoMensaje(texto: string): string | null {
  const limpio = texto.trim();
  if (!limpio) return "Escribí el mensaje";
  return limpio.length > MAX_TEXTO_MENSAJE ? `Hasta ${MAX_TEXTO_MENSAJE} caracteres` : null;
}
